
import {
  defineComponent,
  h,
  PropType,
  ref,
  toRefs,
  useRoute,
  useRouter,
  watch,
  vue3Model,
  onMounted,
  onUnmounted,
  nextTick,
  computed
} from '~/utils/nuxt3-migration'
import { VNode, VNodeChildren } from 'vue'
import CLink from '~/components/shared/configurable/link/CLink.vue'
import CIcon from '~/components/shared/configurable/CIcon.vue'
import { useTabRef } from '~/compositions/tab/ref'
import { useTabs } from '~/compositions/tab'
import { useTabsClasses } from '~/compositions/tab/class'
import { noop } from '~/utils/function'
import { TabVariant } from '~/models/app/tab'
import CTabMenu from '~/components/shared/configurable/tab/CTabMenu.vue'
import { CIcon as CIconType } from '~/icons/types'
import { ciAngleDown } from '~/icons/source/regular/angle-down'
import { ciAngleRight } from '~/icons/source/regular/angle-right'
import { ciAngleLeft } from '~/icons/source/regular/angle-left'
import { useId } from '~/compositions/id'
import { useScroll } from '~/compositions/scroll'

export default defineComponent({
  model: vue3Model,
  props: {
    line: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: Number,
      default: 0
    },
    nav: {
      type: Boolean,
      default: false
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    // Can be used alongside nav, when the tabs navigate to a query instead of a page/path
    // The route match checks are performed on the full path instead of the path
    queryNav: {
      type: Boolean,
      default: false
    },
    // Can be used alongside nav, when you don't care about the query of the routes in the tabs but only the names
    // it's like the opposite of the queryNav prop above
    nameNav: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String as PropType<TabVariant>,
      default: 'secondary'
    },
    contentClass: {
      type: String,
      default: '',
      required: false
    },
    navClass: {
      type: String,
      default: '',
      required: false
    },
    tabClass: {
      type: String,
      default: '',
      required: false
    },
    activeTabClass: {
      type: String,
      default: '',
      required: false
    }
  },
  setup(props, { slots, emit }: any) {
    const {
      line,
      nav,
      queryNav,
      nameNav,
      variant,
      fullWidth,
      contentClass,
      navClass,
      tabClass,
      activeTabClass
    } = toRefs(props)

    const route = useRoute()
    const router = useRouter()
    const { uuid } = useId()
    const { scrollTo } = useScroll()

    const tabsRandomId = `tabs${uuid()}`
    const selectedTabIndex = ref(props.modelValue)
    const hasNavButtons = ref(false)
    const hasLeftOverflow = ref(false)
    const hasRightOverflow = ref(false)
    const tabsResizeObserver = ref(null)
    const previousTabPixels = ref<number>()
    const activeTabPixels = ref<number>()
    const direction = ref('left')
    const mounted = ref(false)
    const isScrolling = ref(false)

    const { tabIsActive, getActiveTab } = useTabs(selectedTabIndex)
    const { getTabRefName, getNavRefName } = useTabRef()
    const {
      getTabClasses,
      getTabsContainerClasses,
      getContainerClass,
      getNavClasses,
      getContentClasses,
      getSelectedTabClasses
    } = useTabsClasses(line.value)

    watch(
      () => props.modelValue,
      newValue => {
        if (newValue !== selectedTabIndex.value) {
          selectTab(newValue)
        }
      }
    )

    watch(route, () => {
      if (nav.value) {
        const tabRouteIndex = tabsRouteNames.value.indexOf(route.value.name)
        setTimeout(() => {
          if (!tabIsActive(tabRouteIndex) && tabRouteIndex > -1) {
            selectTab(tabRouteIndex)
          }
        }, 100)
      }
    })

    watch(hasNavButtons, () => {
      scrollToTabIfOverflown(selectedTabIndex.value, !!hasNavButtons.value)
    })

    onMounted(() => {
      mounted.value = true
      scrollToTabIfOverflown(selectedTabIndex.value)
      const tabsEl = getTabsEl()
      if (tabsEl) {
        tabsResizeObserver.value = new ResizeObserver(() =>
          onTabsResize(tabsEl)
        )
        tabsResizeObserver.value.observe(tabsEl)
      }
    })

    onUnmounted(() => {
      if (tabsResizeObserver.value) {
        tabsResizeObserver.value.disconnect()
      }
    })

    const tabsRouteNames = computed(() =>
      getTabs().map(tab => {
        const to = tab.componentOptions.propsData.to
        if (typeof to === 'string' && to.startsWith('/')) {
          return router.resolve({ path: to }).route.name
        }
        return to?.name
      })
    )

    async function selectTab(index: number) {
      previousTabPixels.value = getTabLeftPixels(selectedTabIndex.value)
      activeTabPixels.value = getTabLeftPixels(index)
      if (selectedTabIndex.value > index) {
        direction.value = 'left'
      } else {
        direction.value = 'right'
      }

      selectedTabIndex.value = index
      scrollToTabIfOverflown(index)

      emit('update:modelValue', index)
      await nextTick()
      animate(line.value ? 400 : 300)
    }

    function animate(duration = 300) {
      if (process.server) {
        return
      }
      const selectedTabElement = getTabsEl()?.children[
        selectedTabIndex.value
      ].querySelector(line.value ? '.line' : '.pill')
      if (!selectedTabElement) {
        return
      }
      let left = parseFloat(selectedTabElement?.style?.left || 0)
      const targetPosition = 0
      const startPosition = left
      let velocity = 0
      const acceleration = 0.005
      let startTime = null

      function update(currentTime) {
        if (startTime === null) {
          startTime = currentTime
        }

        const elapsedTime = currentTime - startTime
        const progress = Math.min(elapsedTime / duration, 1)

        velocity += acceleration * (direction.value === 'left' ? -1 : 1)

        const maxVelocity = Math.abs(targetPosition - startPosition) / duration
        velocity = Math.max(Math.min(velocity, maxVelocity), -maxVelocity)

        left += velocity * (direction.value === 'left' ? -1 : 1)

        const easedProgress = easeOutQuadratic(progress)
        left = startPosition + (targetPosition - startPosition) * easedProgress

        selectedTabElement.style.left = `${left}px`

        if (elapsedTime >= duration || progress === 1) {
          selectedTabElement.style.left = '0px'
          return false
        }

        return true
      }

      function animateFrame(currentTime) {
        if (update(currentTime)) {
          requestAnimationFrame(animateFrame)
        }
      }

      function easeOutQuadratic(x) {
        return 1 - (1 - x) * (1 - x)
      }

      requestAnimationFrame(animateFrame)
    }

    function getTabTitleContent(node: VNode): VNodeChildren {
      const titleSlot = node.data?.scopedSlots?.title
      const titleProp = node.componentOptions?.propsData?.title
      if (titleSlot) {
        return [titleSlot({})]
      }
      if (titleProp) {
        return titleProp
      }
      return []
    }

    function renderContent(tabs: VNode[]): VNodeChildren {
      if (nav.value) {
        // Do not create the tab content in case of nav tabs
        return []
      }
      if (!slots || !slots.default) {
        return []
      }
      const activeTab = getActiveTab(tabs)

      const activeTabChildren = activeTab?.componentOptions?.children
      if (!activeTabChildren?.length) {
        return []
      }
      return [
        h(
          'div',
          { class: [...getContentClasses(), contentClass.value] },
          activeTabChildren || []
        )
      ]
    }

    function getTabsEl() {
      return document.getElementById(tabsRandomId)
    }

    function getTabLeftPixels(index: number) {
      if (process.client) {
        const tabsEl = getTabsEl()
        return tabsEl?.children[index].getBoundingClientRect().left
      }
      return undefined
    }

    function onTabsResize(tabsEl: HTMLElement) {
      setTimeout(() => {
        hasNavButtons.value = tabsEl.scrollWidth > tabsEl.clientWidth
        checkOverflow(tabsEl)
      }, 200)
    }

    function checkLeftOverflow(tabsEl: HTMLElement) {
      hasLeftOverflow.value = tabsEl.scrollLeft > 0
    }

    function checkRightOverflow(tabsEl: HTMLElement) {
      hasRightOverflow.value =
        tabsEl.scrollLeft + tabsEl.clientWidth < tabsEl.scrollWidth
    }

    function checkOverflow(tabsEl: HTMLElement) {
      checkLeftOverflow(tabsEl)
      checkRightOverflow(tabsEl)
    }

    function renderTabs(tabs: VNode[]): VNodeChildren {
      function createTabElement(node: VNode, index: number) {
        const tabProps = node.componentOptions?.propsData as any
        const disabled = tabProps.disabled === true || tabProps.disabled === ''
        function getTag() {
          return nav.value ? CLink : 'button'
        }
        function getProps() {
          if (nav.value) {
            return {
              to: tabProps.to,
              href: tabProps.href,
              target: tabProps.target,
              useExternalLinkMediator: tabProps.useExternalLinkMediator,
              disabled
            }
          }
          return {}
        }
        function getListeners() {
          if (disabled) {
            return {
              click: noop
            }
          }
          return {
            click: (e: MouseEvent) => {
              setTimeout(() => selectTab(index), 10)
              const clickListener = node?.componentOptions?.listeners?.click
              if (clickListener) {
                clickListener(e)
              }
            }
          }
        }

        function getActiveTab(isActive: boolean) {
          if (isActive) {
            const left =
              direction.value === 'left'
                ? previousTabPixels.value - activeTabPixels.value
                : -(activeTabPixels.value - previousTabPixels.value)

            return h('span', {
              style: { left: Math.abs(left) && `${left}px` },
              class: getSelectedTabClasses(variant.value)
            })
          }
        }

        function getTab(customClass?: string) {
          return h(
            getTag(),
            {
              attrs: {
                disabled: nav.value ? false : disabled,
                ...node.data?.attrs
              },
              props: getProps(),
              staticClass:
                'tw-whitespace-nowrap tab-button tw-box-content tw-relative',
              class: [
                ...getTabClasses(
                  tabIsActive(index),
                  disabled,
                  variant.value,
                  fullWidth.value
                ),
                customClass,
                ...extraClasses
              ],
              ref: getTabRefName(index),
              on: getListeners()
            },
            [
              getIcon(tabProps.icon, ['tw-mr-2 tw-relative tw-z-10']),
              h('div', { class: ['tw-relative tw-z-10'] }, [
                getTabTitleContent(node)
              ]),
              tabProps.menu
                ? getIcon(ciAngleDown, ['tw-ml-2 tw-relative tw-z-10'])
                : null,
              getActiveTab(tabIsActive(index))
            ]
          )
        }

        const extraClasses = []
        if (tabClass.value) {
          extraClasses.push(tabClass.value)
        }
        if (activeTabClass.value && tabIsActive(index)) {
          extraClasses.push(activeTabClass.value)
        }

        if (tabProps.menu) {
          return h(
            CTabMenu,
            { props: { menu: tabProps.menu, variant: variant.value } },
            [h('template', { slot: 'buttonSlot' }, [getTab('tw-h-6')])]
          )
        }

        return getTab()
      }

      return tabs.map((node: VNode, index: number) =>
        createTabElement(node, index)
      )
    }

    function setSelectedTabIndexByPath(tabs: VNode[]) {
      tabs.forEach((tab, index) => {
        const props: any = tab.componentOptions?.propsData
        if (!props) {
          return
        }
        const { href, to } = props

        if (nameNav.value && route.value.name === to?.name) {
          selectTab(index)
          return
        }

        let toAsPath
        if (typeof to === 'string') {
          toAsPath = to
        } else if (typeof href === 'string') {
          toAsPath = href
        } else {
          const href = router.resolve(to)?.href
          if (!href.endsWith('/')) {
            toAsPath = href + '/'
          } else {
            toAsPath = href
          }
        }
        let pathToCompare = queryNav.value
          ? route.value.fullPath
          : route.value.path

        // Remove hash from path if present
        pathToCompare = pathToCompare.split('#')[0]

        if (toAsPath?.endsWith('/') && !pathToCompare.endsWith('/')) {
          pathToCompare += '/'
        }

        if (
          toAsPath &&
          (pathToCompare.startsWith(href) ||
            pathToCompare.startsWith(toAsPath)) &&
          !mounted.value
        ) {
          selectTab(index)
        }
      })
    }
    function getIcon(icon: CIconType | null, classes: string[]) {
      if (icon) {
        return [
          h(CIcon, {
            props: { icon, size: 20 },
            class: classes
          })
        ]
      }
      return null
    }

    function prevTab() {
      const tabs = getTabs()
      if (selectedTabIndex.value === 0) {
        return
      }
      let index = selectedTabIndex.value
      tabs.slice(0, selectedTabIndex.value).forEach((tab, i) => {
        if (!tab.componentOptions.propsData.disabled) {
          index = i
        }
      })

      if (nav.value) {
        const { propsData } = tabs[index].componentOptions
        handleNavClick(propsData)
      }
      selectTab(index)
    }
    function nextTab() {
      const tabs = getTabs()
      if (selectedTabIndex.value === tabs.length - 1) {
        return
      }
      let index = selectedTabIndex.value
      if (selectedTabIndex.value < tabs.length) {
        tabs.slice(selectedTabIndex.value + 1).some((tab, i) => {
          if (!tab.componentOptions.propsData.disabled) {
            index = selectedTabIndex.value + 1 + i
            return true
          }
          return false
        })
        if (nav.value) {
          const { propsData } = tabs[index].componentOptions
          handleNavClick(propsData)
        }
        selectTab(index)
      }
    }

    function handleNavClick(propsData: object) {
      if (propsData.href) {
        window.location.href = propsData.href
      } else if (typeof propsData.to === 'object') {
        router.push(propsData.to)
      } else {
        router.push({ path: propsData.to })
      }
    }

    function getTabs() {
      return ((slots?.default && slots.default()) || []).filter(
        (node: VNode) => node.componentOptions?.tag === 'CTab'
      )
    }

    function scrollToTabIfOverflown(
      index: number,
      isNavButtonsVisible = false
    ) {
      if (process.server) {
        return
      }
      const tabs = getTabsEl()
      if (!tabs) {
        return
      }

      const tabsElements = getTabsEl().children
      if (!tabsElements) {
        return
      }
      const tabEl = tabsElements[index]
      if (!tabEl) {
        return
      }
      const tabsNavRef = tabEl.parentElement
      if (!tabsNavRef) {
        return
      }

      // Check for overflow after scrolling
      checkOverflow(tabsNavRef)

      const navigationButtonsWidth = isNavButtonsVisible ? 112 : 0
      const tabIsOverflownInNav =
        tabEl.offsetLeft < tabsNavRef.scrollLeft ||
        tabEl.offsetLeft +
          (tabEl.clientWidth +
            navigationButtonsWidth +
            parseFloat(getComputedStyle(tabEl).marginLeft)) >=
          tabsNavRef.offsetWidth + tabsNavRef.scrollLeft

      if (!tabIsOverflownInNav || isScrolling.value) {
        return
      }
      scrollTo(tabEl, {
        duration: 300,
        easing: 'ease-out',
        offset: -10,
        force: false,
        container: tabsNavRef,
        onStart: () => (isScrolling.value = true),
        onDone: () => (isScrolling.value = false),
        y: false,
        x: true
      })

      // Check for overflow after scrolling
      setTimeout(() => checkOverflow(tabsNavRef), 350)
    }

    function getNavButton(
      icon: CIconType,
      classes: string,
      isDisabled: boolean,
      onClick = noop,
      isLeftButton = false
    ) {
      return h(
        'CButton',
        {
          props: {
            tertiary: true,
            variant: 'neutral'
          },
          class: [
            classes,
            'tw-h-10 tw-min-w-10 !tw-px-0 tw-flex tw-items-center',
            { 'tw-cursor-default': isDisabled },
            isLeftButton
              ? hasNavButtons.value && hasLeftOverflow.value
                ? 'tw-block'
                : 'tw-hidden'
              : hasNavButtons.value && hasRightOverflow.value
              ? 'tw-block'
              : 'tw-hidden'
          ],
          on: { click: onClick }
        },
        [getIcon(icon)]
      )
    }

    return () => {
      const tabs = getTabs()
      if (nav.value) {
        setSelectedTabIndexByPath(tabs)
      }
      return h('div', { staticClass: 'tw-flex', class: getContainerClass() }, [
        h(
          'div',
          {
            class: getTabsContainerClasses()
          },
          [
            getNavButton(
              ciAngleLeft,
              'tw-mr-2 md:tw-mr-4',
              selectedTabIndex.value === 0,
              prevTab,
              true
            ),
            h(
              'nav',
              {
                attrs: {
                  id: tabsRandomId
                },
                staticClass:
                  'tw-flex tw-overflow-auto tw-flex-nowrap tw-hide-scrollbar tw-h-10 tw-relative',
                ref: getNavRefName(),
                class: [...getNavClasses(), navClass.value],
                on: {
                  scroll: e => checkOverflow(e.target)
                }
              },
              renderTabs(tabs)
            ),
            getNavButton(
              ciAngleRight,
              'tw-ml-2 md:tw-ml-4',
              selectedTabIndex.value === getTabs().length - 1,
              nextTab,
              false
            )
          ]
        ),
        renderContent(tabs)
      ])
    }
  }
})
